import React, { useRef } from 'react';
import { Avatar, Tooltip, Modal, message } from 'antd';
import {
  EditOutlined,
  UserOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import {
  useGetAvatarQuery,
  useUploadAvatarMutation,
  useDeleteAvatarMutation,
} from 'entities/user';
import { useIntl } from 'react-intl';

type Props = {
  size: number;
};

export const UserAvatar: React.FC<Props> = ({ size }) => {
  const { formatMessage: t } = useIntl();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: avatarUrl, refetch } = useGetAvatarQuery();
  const [uploadAvatar] = useUploadAvatarMutation();
  const [deleteAvatar] = useDeleteAvatarMutation();

  const handleEditClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      await uploadAvatar(file).unwrap();
      await refetch();
      message.success(t({ id: 'avatar.update.success' }));
    } catch (error) {
      message.error(t({ id: 'avatar.update.error' }));
    }
  };

  const handleDelete = () => {
    Modal.confirm({
      title: t({ id: 'avatar.delete.title' }),
      okText: t({ id: 'avatar.delete.ok' }),
      cancelText: t({ id: 'avatar.delete.cancel' }),
      okButtonProps: { danger: true },
      icon: <DeleteOutlined />,
      onOk: async () => {
        try {
          await deleteAvatar().unwrap();
          message.success(t({ id: 'avatar.delete.success' }));
          refetch();
        } catch {
          message.error(t({ id: 'avatar.delete.error' }));
        }
      },
    });
  };

  return (
    <div style={styles.avatarContainer}>
      <Avatar
        size={size}
        src={avatarUrl ?? undefined}
        icon={!avatarUrl ? <UserOutlined /> : undefined}
        style={styles.avatar}
      />
      <Tooltip title={t({ id: 'avatar.change.title' })}>
        <div style={styles.editIcon} onClick={handleEditClick}>
          <EditOutlined />
        </div>
      </Tooltip>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />
      {avatarUrl && (
        <div style={styles.deleteWrapper} onClick={handleDelete}>
          <DeleteOutlined style={styles.deleteIcon} />
        </div>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  avatarContainer: {
    position: 'relative',
    width: 'fit-content',
  },
  avatar: {
    border: '1px solid #e0e0e0',
  },
  editIcon: {
    position: 'absolute',
    right: -6,
    top: -6,
    width: 28,
    height: 28,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#fff',
    borderRadius: '50%',
    padding: 4,
    boxShadow: '0 0 4px rgba(0, 0, 0, 0.15)',
    cursor: 'pointer',
  },
  deleteWrapper: {
    marginTop: 8,
    textAlign: 'center',
    cursor: 'pointer',
  },
  deleteIcon: {
    color: 'red',
  },
};
